// Team dashboard view (/dashboard): read-only team overview.
// Strings localized via i18n (English default, RU parity). Pure render; navigation injected.

import type { DashboardMember } from '../dashboard.js';
import { t } from './i18n.js';
import { copyText } from './copy-text.js';
// DS button for "Leave team" (registers the custom element + its light-DOM CSS).
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-button.css';

interface DashboardViewOpts {
  data: {
    team: { id: string; name: string } | null;
    role: string | null;
    joinCode: string | null;
    members: DashboardMember[];
    selfUserId: string | null;
    quota?: { used: number; limit: number } | null;
    joinError?: string | null;
    inviteMsg?: string | null;
  };
  navigate: (path: string) => void;
  onJoin: (code: string) => void;
  onLeave: () => void;
  onRotateCode: () => void;
  onInvite: (email: string) => void;
  onRemove: (userId: string) => void;
}

/**
 * Ответ на приглашение → какое сообщение показать под формой.
 *
 * `limit` — отдельное состояние, а не «ошибка»: воркер держит суточный потолок
 * приглашений на команду (B-260927-abuse-no-rate-limit), и «попробуй ещё раз»
 * звало бы повторять то, что до завтра не получится.
 */
export function inviteMsgFor(r: { ok: boolean; error?: string }): 'sent' | 'limit' | 'error' {
  if (r.ok) return 'sent';
  return r.error === 'too_many_invites' ? 'limit' : 'error';
}

function roleLabel(role: string | null): string { return role === 'owner' ? t('dashboard.roleOwner') : t('dashboard.roleMember'); }
function initial(email: string | null): string { return (email ?? '?').charAt(0).toUpperCase(); }

const QUOTA_WARN_PCT = 80;
const bytesToMb = (n: number): number => Math.round(n / (1024 * 1024));

/**
 * Storage quota bar for a team: fill width = used/limit, yellow from 80%, red at 100%.
 * Numbers come from the server (dashboard payload) — no hardcoded limit here.
 */
export function renderQuotaBar(q: { used: number; limit: number }): HTMLElement {
  const pct = q.limit > 0 ? (q.used / q.limit) * 100 : 0;
  const wrap = document.createElement('div');
  wrap.className = 'quota';
  const track = document.createElement('div');
  track.className = 'quota-track';
  const fill = document.createElement('div');
  fill.className = 'quota-fill' + (pct >= 100 ? ' is-full' : pct >= QUOTA_WARN_PCT ? ' is-warn' : '');
  fill.style.width = `${Math.min(100, Math.round(pct))}%`;
  track.appendChild(fill);
  const label = document.createElement('div');
  label.className = 'quota-label';
  label.textContent = t('dashboard.quotaLabel', { used: bytesToMb(q.used), limit: bytesToMb(q.limit) });
  wrap.appendChild(track);
  wrap.appendChild(label);
  return wrap;
}

export function mountDashboardView(host: HTMLElement, opts: DashboardViewOpts): void {
  host.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'dashboard-view';

  const back = document.createElement('button');
  back.dataset.role = 'back';
  back.className = 'dash-back';
  back.textContent = t('dashboard.back');
  back.addEventListener('click', () => opts.navigate('/library'));
  root.appendChild(back);

  const { team, role, joinCode, members, selfUserId, quota, joinError, inviteMsg } = opts.data;

  if (!team) {
    // Join form is the empty state — the web can now join a team directly
    // (the old "…in the desktop app" copy was removed as contradictory).
    // Join form — lets a member join a team by code.
    const joinSection = document.createElement('section');
    joinSection.className = 'dash-join';
    const joinTitle = document.createElement('h2');
    joinTitle.className = 'dash-join-title';
    joinTitle.textContent = t('dashboard.joinTitle');
    joinSection.appendChild(joinTitle);
    const joinHint = document.createElement('p');
    joinHint.className = 'dash-join-hint';
    joinHint.textContent = t('dashboard.joinHint');
    joinSection.appendChild(joinHint);
    const joinInput = document.createElement('input');
    joinInput.dataset.role = 'join-input';
    joinInput.className = 'dash-join-input';
    joinInput.type = 'text';
    joinInput.placeholder = t('dashboard.joinPlaceholder');
    joinSection.appendChild(joinInput);
    const joinBtn = document.createElement('button');
    joinBtn.dataset.role = 'join-submit';
    joinBtn.className = 'dash-join-btn';
    joinBtn.textContent = t('dashboard.joinBtn');
    joinBtn.addEventListener('click', () => {
      const code = joinInput.value.trim();
      if (!code) return;
      opts.onJoin(code);
    });
    joinSection.appendChild(joinBtn);
    if (joinError) {
      const errEl = document.createElement('p');
      errEl.dataset.role = 'join-error';
      errEl.className = 'dash-join-error';
      errEl.textContent = joinError === 'invalid_code'
        ? t('dashboard.joinErrorInvalid')
        : t('dashboard.joinErrorGeneric');
      joinSection.appendChild(errEl);
    }
    root.appendChild(joinSection);

    host.appendChild(root);
    return;
  }

  const banner = document.createElement('section');
  banner.className = 'dash-banner';

  const left = document.createElement('div');
  const h = document.createElement('h1');
  h.className = 'dash-team';
  h.textContent = team.name;
  left.appendChild(h);
  const selfBadge = document.createElement('span');
  selfBadge.dataset.role = 'self-role';
  selfBadge.className = `dash-badge dash-badge-${role === 'owner' ? 'owner' : 'member'}`;
  selfBadge.textContent = roleLabel(role);
  left.appendChild(selfBadge);
  banner.appendChild(left);

  if (joinCode) {
    const codeWrap = document.createElement('div');
    codeWrap.dataset.role = 'join-code';
    codeWrap.className = 'dash-code';
    const lbl = document.createElement('div');
    lbl.className = 'dash-code-label';
    lbl.textContent = t('dashboard.joinCode');
    const codeRow = document.createElement('div');
    codeRow.className = 'dash-code-row';
    const code = document.createElement('code');
    code.textContent = joinCode;
    const copy = document.createElement('button');
    copy.dataset.role = 'copy-code';
    copy.className = 'dash-copy';
    copy.textContent = t('dashboard.copy');
    // Через общий copyText(): он отвечает, получилось ли. Раньше кнопка писала
    // «Copied» всегда — и на отказе буфера (нет разрешения, приватное окно),
    // когда в чат уехало бы то, что лежало в буфере до этого
    // (B-260927-share-web-small-ui-bugs). Поля с кодом рядом нет — только
    // <code>, — поэтому фолбэка через выделение здесь нет.
    copy.addEventListener('click', async () => {
      const ok = await copyText(joinCode);
      copy.textContent = ok ? t('dashboard.copied') : t('dashboard.copyFailed');
      setTimeout(() => { copy.textContent = t('dashboard.copy'); }, 1500);
    });
    codeRow.appendChild(code);
    codeRow.appendChild(copy);
    const rotate = document.createElement('button');
    rotate.dataset.role = 'rotate-code';
    rotate.className = 'dash-rotate';
    rotate.textContent = t('dashboard.rotate');
    rotate.addEventListener('click', () => opts.onRotateCode());
    codeRow.appendChild(rotate);
    codeWrap.appendChild(lbl);
    codeWrap.appendChild(codeRow);
    banner.appendChild(codeWrap);
  }
  root.appendChild(banner);

  // Storage quota bar — team usage against the shared limit (visible to the team).
  if (quota) {
    root.appendChild(renderQuotaBar(quota));
  }

  // Invite section — owner only
  if (role === 'owner') {
    const inviteSection = document.createElement('section');
    inviteSection.dataset.role = 'invite';
    inviteSection.className = 'dash-invite';
    const inviteTitle = document.createElement('h2');
    inviteTitle.className = 'dash-invite-title';
    inviteTitle.textContent = t('dashboard.inviteTitle');
    inviteSection.appendChild(inviteTitle);
    const inviteInput = document.createElement('input');
    inviteInput.dataset.role = 'invite-input';
    inviteInput.className = 'dash-invite-input';
    inviteInput.type = 'email';
    inviteInput.placeholder = t('dashboard.invitePlaceholder');
    inviteSection.appendChild(inviteInput);
    const inviteSubmit = document.createElement('button');
    inviteSubmit.dataset.role = 'invite-submit';
    inviteSubmit.className = 'dash-invite-btn';
    inviteSubmit.textContent = t('dashboard.inviteBtn');
    inviteSubmit.addEventListener('click', () => {
      const email = inviteInput.value.trim();
      if (!email) return;
      opts.onInvite(email);
    });
    inviteSection.appendChild(inviteSubmit);
    if (inviteMsg) {
      const msgEl = document.createElement('p');
      msgEl.dataset.role = 'invite-msg';
      msgEl.className = 'dash-invite-msg';
      msgEl.textContent = inviteMsg === 'sent'
        ? t('dashboard.inviteSent')
        : inviteMsg === 'limit'
          ? t('dashboard.inviteLimit')
          : t('dashboard.inviteError');
      inviteSection.appendChild(msgEl);
    }
    root.appendChild(inviteSection);
  }

  const list = document.createElement('section');
  list.className = 'dash-members';
  const mhead = document.createElement('div');
  mhead.dataset.role = 'members-head';
  mhead.className = 'dash-mhead';
  mhead.textContent = t('dashboard.members', { n: members.length });
  list.appendChild(mhead);
  for (const m of members) {
    const row = document.createElement('div');
    row.dataset.role = 'member-row';
    row.className = 'dash-row';
    const av = document.createElement('div');
    av.className = 'dash-av';
    av.textContent = initial(m.email);
    const email = document.createElement('div');
    email.className = 'dash-email';
    email.textContent = m.email ?? t('dashboard.unknownEmail');
    const rbadge = document.createElement('span');
    rbadge.className = `dash-badge dash-badge-${m.role === 'owner' ? 'owner' : 'member'}`;
    rbadge.textContent = roleLabel(m.role);
    row.appendChild(av);
    row.appendChild(email);
    row.appendChild(rbadge);
    if (selfUserId && m.userId === selfUserId) {
      const youMark = document.createElement('span');
      youMark.className = 'dash-you';
      youMark.textContent = t('dashboard.youSuffix');
      row.appendChild(youMark);
    }
    if (role === 'owner' && m.userId !== selfUserId) {
      const removeBtn = document.createElement('button');
      removeBtn.dataset.role = 'remove-member';
      removeBtn.className = 'dash-remove';
      removeBtn.setAttribute('aria-label', t('dashboard.removeMember'));
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', () => opts.onRemove(m.userId));
      row.appendChild(removeBtn);
    }
    list.appendChild(row);
  }
  root.appendChild(list);

  // Leave button — only for non-owners. Set apart in its own footer row (top
  // hairline + spacing) so this mild-destructive action doesn't crowd the member
  // list; rendered as a neutral DS bw-button (was an unstyled UA <button>).
  if (role !== 'owner') {
    const leaveRow = document.createElement('div');
    leaveRow.className = 'dash-leave-row';
    const leaveBtn = document.createElement('bw-button');
    leaveBtn.dataset.role = 'leave';
    leaveBtn.className = 'dash-leave';
    leaveBtn.setAttribute('size', 'sm');
    leaveBtn.setAttribute('shape', 'pill');
    leaveBtn.textContent = t('dashboard.leaveBtn');
    leaveBtn.addEventListener('bw-click', () => opts.onLeave());
    leaveRow.appendChild(leaveBtn);
    root.appendChild(leaveRow);
  }

  host.appendChild(root);
}
